<template>
    <div class="qw-chat">
        <div class="chat-messages" ref="chatMessages">
            <p class="no-messages" v-if="!hasMessages">
                <span class="icon"><i class="fas fa-comments"></i></span>
                <span class="text">{{ __('noMessages', this.translations.chatbot) }}</span>
            </p>
            <qw-message v-for="(message, index) in messages" 
                :key="`m-${index}`" 
                :reply="message.reply" 
                :sender="message.sender || defaultSender">
                <qw-error v-if="message.type == 'error'"
                    :key="index"
                    :reply="message.reply"
                    @click="resetConversation()">
                    {{ message.text }}
                </qw-error>
                <qw-text-message v-if="message.type == 'text'"
                    :key="index"
                    :sender="message.sender || defaultSender"
                    :reply="message.reply">
                    {{ message.text }}
                </qw-text-message>
                <qw-options v-if="message.type == 'options'"
                    :key="index"
                    :sender="message.sender || defaultSender" 
                    :options="message.options" 
                    :reply="message.reply"
                    @selected="optionChosen($event)">
                </qw-options>
                <qw-table v-if="message.type == 'table'"
                    :key="index"
                    :columns="message.headers" 
                    :rows="message.rows">
                </qw-table>
                <qw-list v-if="message.type == 'list'"
                    :key="index"
                    :title="message.title" 
                    :rows="message.items" >
                </qw-list>
                <qw-card v-if="message.type == 'card'"
                    :key="index"
                    :sender="message.sender || defaultSender"
                    :title="message.title"
                    :body="message.textBody"
                    :image="message.imageUrl"
                    :url="message.actionUrl">
                </qw-card>
            </qw-message>
            <qw-typing v-if="isTyping"></qw-typing>
        </div>
        <div class="chat-input">
            <input type="text" 
                class="input" 
                :placeholder="__('placeholder', this.translations.chatbot)" 
                v-model="messageInput" 
                @keydown.enter="send()"/>
            <button class="send-btn" @click="send()"><i class="fas fa-arrow-right"></i></button>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import ChatbotService from "@/services/ChatbotService";
import QwError from "@/components/chatbot/QWError";
import QwTextMessage from "@/components/chatbot/QWTextMessage";
import QwOptions from "@/components/chatbot/QWOptions";
import QwTable from "@/components/chatbot/QWTable";
import QwList from "@/components/chatbot/QWList";
import QwCard from "@/components/chatbot/QWCard";
import QwTyping from "@/components/chatbot/QWTyping";

import QwMessage from "@/components/chatbot/QWMessage";

export default {
    name: "qw-chat",
    components: {
        QwError,
        QwTextMessage,
        QwOptions,
        QwTable,
        QwList,
        QwCard,
        QwTyping,
        QwMessage
    },
    data() {
        return {
            messages: [],
            messageInput: "",
            token: null,
            isTyping: false
        }
    },
    mounted() {
        this.init();
    },
    methods: {
        async init() {
            await this.startConversation();
        },
        async startConversation() {
            this.isTyping = true;
            try {
                let response = await ChatbotService.start();
                if(!response.data.success)
                    return;
                
                this.token = response.data.content.token;
                this.messages = this.messages.concat(response.data.content.greetingMessages);
            } catch(e) {
                this.throwErrorMessage();
            }
            this.isTyping = false;
        },
        resetConversation() {
            this.messages = [];
            this.isTyping = false;
            this.token = null;
            this.messageInput = "";
            this.init();
        },
        throwErrorMessage() {
            this.messages.push({ 
                type: "error", 
                text: "Error! Click here to reset the conversation.",
                reply: false 
            });
        },
        scrollToChatBottom() {
            let element = this.$refs.chatMessages;
            element.scrollTop = element.scrollHeight;
        },
        optionChosen(option) {
            this.messageInput = option.value || option.label;
            this.sendMessage();
        },
        async send() {
            if(!this.canSendMessage)
                return;
                
            if(this.messageInput.toLowerCase().includes('i speak')) {
                this.changeLanguage();
                return;
            } else if (this.messageInput.toLowerCase() === "clear") {
                this.clearChat();
                return;
            } else if (this.messageInput.toLowerCase() == "go dark") {
                this.changeTheme("dark");
                return;
            } else if (this.messageInput.toLowerCase() == "go light") {
                this.changeTheme("light");
                return;
            } 

            await this.sendMessage();
        },
        changeLanguage() {
            let language = this.messageInput.toLowerCase().replace("i speak", "").trim();
            if(language == "english" || language == "arabic")
                this.$store.dispatch("setLanguage", language == "english" ? "en" : "ar");
            this.messageInput = "";
        },
        changeTheme(theme) {
            this.$store.dispatch("changeTheme", theme);
            this.messageInput = "";
        },
        clearChat() {
            this.messages = [];
            this.messageInput = "";
        },
        async sendMessage() {
            let textToSend = this.messageInput.trim();
            
            let messageObject = { sender: "You", type: "text", text: textToSend, reply: false };
            this.messages.push(messageObject);
            this.messageInput = "";

            await this.getReply(textToSend);
        },
        async getReply(input) {
            this.isTyping = true;
            try {
                let response = await ChatbotService.getReply(input, this.token);
                if(!response.data.success)
                    return;
                this.messages = this.messages.concat(response.data.content.messages);
            } catch(e) {
                this.throwErrorMessage();
            }
            this.isTyping = false;
        },
    },
    computed: {
        defaultSender() {
            return "Chatbot";
        },
        hasMessages() {
            return !!(this.messages.length);
        },
        canSendMessage() {
            return !!(this.messageInput && this.messageInput.trim().length);
        }
    },
    watch: {
        messages() {
            Vue.nextTick(() => {
                this.scrollToChatBottom();
            });
        },
    }
}
</script>

<style lang="scss" scoped>
.qw-chat {
    position: absolute;
    bottom: calc(100% + 10px);
    right: 0;
    width: 350px;
    height: 450px;
    background-color: $lighter-gray;
    border-radius: 5px;
    box-shadow: 0px 0px 5px 3px $light-gray;
    .chat-messages {
        display: flex;
        flex-direction: column;
        position: absolute;
        width: 100%;
        height: calc(100% - #{$chatbot-input-height});
        top: 0;
        left: 0;
        padding: 15px;
        overflow-y: auto;
        .no-messages {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            width: 100%;
            height: 100%;
            font-size: 13px;
            font-family: $lato;
            font-weight: normal;
            color: $gray;
            .icon {
                font-size: 25px;
                margin: 0;
            }
        }
    }
    .chat-input {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        position: absolute;
        width: 100%;
        height: $chatbot-input-height;
        bottom: 0;
        left: 0;
        padding: 0px 15px;
        color: $darker-gray;
        border-top: 1px solid $light-gray!important;
        .input {
            display: flex;
            flex-grow: 1;
            height: 100%;
            font-family: $lato;
            font-size: 14px;
            font-weight: normal;
            background: none;
            @include removeBootstrapOutline;
        }
        .send-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            background: none;
            font-size: 18px;
            color: $dark-gray;
            @include removeBootstrapOutline;
            @include fastestTransition;
            &:hover {
                color: $darker-gray;
            }
        }
    }
    &:before {
        content: '';
        position: absolute;
        top: calc(100% - 2px);
        right: 15px;
        border: 10px solid;
        border-color: $lighter-gray transparent transparent transparent;
    }
}

@media screen and (max-width: $breakpoint-sm) {
    .qw-chat {
        width: 300px;
        height: 600px;
        &:before {
            top: calc(100% - 2px);
            right: 13px;
            border: 7px solid;
            border-color: $lighter-gray transparent transparent transparent;
        }
    }
}

.dark {
    .qw-chat {
        background-color: $extra-darker-gray;
        box-shadow: none;
        .chat-messages {
            .no-messages {
                color: $darker-gray;
            }
        }
        .chat-input {
            border-top: 1px solid $darkest-gray!important;
            .input {
                color: $gray;
                &::placeholder {
                    color: $darker-gray;
                }
                &:hover {
                    color: $gray;
                }
            }
        }
        &:before {
            border-color: $extra-darker-gray transparent transparent transparent;
        }
    }
}
</style>
